(function () {
  "use strict";

  class ProductCardLabelsComponent extends HTMLElement {
    constructor() {
      super();
      this._isConnected = false;
      this._needsRender = false;
      this._renderRAF = null;
      this._lastHTML = null;
      this._product = null;
      this._activeRenderKey = null;
      this._labelCache = new Map();
      this._renderedVariantId = null;
      this._storefrontToken = undefined;
      this._tokenWarningIssued = false;
    }

    static get observedAttributes() {
      return ["product"];
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      if (this._isConnected) {
        this._scheduleRender();
      } else {
        this._needsRender = true;
      }
    }

    connectedCallback() {
      this._isConnected = true;
      if (this._product && this._needsRender) {
        this._scheduleRender();
        this._needsRender = false;
      } else if (this._product && this._lastHTML == null) {
        this._scheduleRender();
      }
    }

    disconnectedCallback() {
      this._isConnected = false;
      this._activeRenderKey = null;
      if (this._renderRAF) {
        cancelAnimationFrame(this._renderRAF);
        this._renderRAF = null;
      }
    }

    attributeChangedCallback(name, oldVal, newVal) {
      if (name === "product" && newVal && newVal !== oldVal) {
        try {
          const parsed = JSON.parse(newVal);
          this.product = parsed;
        } catch (e) {
          // ignore invalid JSON
        }
      }
    }

    _scheduleRender() {
      if (this._renderRAF) cancelAnimationFrame(this._renderRAF);
      this._renderRAF = requestAnimationFrame(() => {
        this._renderRAF = null;
        this._doRender().catch((error) => {
          console.error("Error rendering product labels:", error);
        });
      });
    }

    async _doRender() {
      const renderKey = {};
      this._activeRenderKey = renderKey;

      if (!this._product) {
        this._renderedVariantId = null;
        this._updateContent("");
        return;
      }

      const variantId = this._getActiveVariantId();
      if (!variantId) {
        this._renderedVariantId = null;
        this._updateContent("");
        return;
      }

      if (variantId !== this._renderedVariantId) {
        this._updateContent("");
      }

      const label = await this._fetchVariantLabel(variantId);

      if (!this._isConnected || this._activeRenderKey !== renderKey) {
        return;
      }

      this._renderedVariantId = variantId;
      const html = this._buildLabelHtml(label);
      this._updateContent(html);
    }

    _getActiveVariantId() {
      if (this._product?.variantId) {
        return String(this._product.variantId);
      }

      if (this._product?.variant?.id) {
        return this._stripVariantId(this._product.variant.id);
      }

      const variants = Array.isArray(this._product?.variants)
        ? this._product.variants
        : [];
      if (variants.length > 0 && variants[0]?.id) {
        return this._stripVariantId(variants[0].id);
      }

      return "";
    }

    async _fetchVariantLabel(variantId) {
      if (!variantId) return "";

      // 1. Determine active Market and Language for context
      const countryCode = window.Shopify?.country || "US";
      
      // Get lang attribute (e.g., 'fr', 'en-US') and convert to valid 2-letter LanguageCode ('FR', 'EN')
      const rawLang = document.documentElement.lang || window.Shopify?.locale || "en";
      const languageCode = rawLang.substring(0, 2).toUpperCase();

      // 2. Make cache key context-aware
      const cacheKey = `${variantId}_${countryCode}_${languageCode}`;

      if (this._labelCache.has(cacheKey)) {
        return this._labelCache.get(cacheKey) || "";
      }

      const token = this._getStorefrontToken();
      if (!token) {
        if (!this._tokenWarningIssued) {
          console.warn("Storefront API token not found");
          this._tokenWarningIssued = true;
        }
        this._labelCache.set(cacheKey, "");
        return "";
      }

      const variantGid = this._toVariantGid(variantId);
      
      // 3. Apply @inContext directive and variables
      const query = `
        query getVariantProductLabel($id: ID!, $country: CountryCode, $language: LanguageCode) 
        @inContext(country: $country, language: $language) {
          node(id: $id) {
            ... on ProductVariant {
              metafield(namespace: "custom", key: "product_label") {
                value
              }
              openToShipDate: metafield(namespace: "stibo", key: "open_to_ship_date") {
                value
              }
            }
          }
        }
      `;

      try {
        const response = await fetch("/api/2025-07/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({
            query,
            variables: { 
                id: variantGid,
                country: countryCode,
                language: languageCode 
            },
          }),
        });

        if (!response.ok) {
          console.error(
            `Error fetching product label: HTTP ${response.status}`
          );
          this._labelCache.set(cacheKey, "");
          return "";
        }

        const data = await response.json();
        if (data?.errors) {
          console.error("Error fetching product label:", data.errors);
          this._labelCache.set(cacheKey, "");
          return "";
        }

        const openToShipDate = data?.data?.node?.openToShipDate?.value;
        if (openToShipDate && this._isNewProduct(openToShipDate)) {
          this._labelCache.set(cacheKey, "New");
          return "New";
        }

        const value =
          data?.data?.node?.metafield?.value != null
            ? String(data.data.node.metafield.value).trim()
            : "";
        this._labelCache.set(cacheKey, value);
        return value;
      } catch (error) {
        console.error("Error fetching product label:", error);
        this._labelCache.set(cacheKey, "");
        return "";
      }
    }

    _buildLabelHtml(rawLabel) {
      const label = typeof rawLabel === "string" ? rawLabel.trim() : "";
      if (!label || label === "#N/A") return "";

      const labelKey = this._normalizeLabelKey(label);
      let display = label;
      let variant = "primary";
      
      // Check for Canada
      const isCA = "" === "CA" || (window.Shopify && window.Shopify.country === "CA");      
      // Check for French (Checking the HTML lang attribute is highly cache-proof!)
      const isFrench = document.documentElement.lang.toLowerCase().startsWith("fr") || "".startsWith("fr");

      switch (labelKey) {
        case "new":
          if(isCA) {
            display = isFrench ? "Nouveau" : "New";
          } else {
            display = "New";
          }
          variant = "primary";
          break;
        case "last-chance":
          if (isCA) {
            display = isFrench ? "Fin de série" : "Discontinued";
            variant = "disabled";
          } else {
            display = "Last Chance";
            variant = "danger";
          }
          break;
        case "discontinued":        
          display = isFrench ? "Fin de série" : "Discontinued";
          variant = "disabled";
          break;
        case "fin-de-serie":
          display = "Fin de série";
          variant = "disabled";
          break;
        default:
          display = label;
          variant = "primary";
          break;
      }

      const span = this._labelSpan(display, variant);
      if (!span) return "";

      return `<style>
          collection-listing[data-view-mode="grid"] .tag-size-custom { font-size: 8px; }
        </style>
        ${span}`;
    }

    _labelSpan(label, variant) {
      const trimmed = typeof label === "string" ? label.trim() : "";
      if (!trimmed) return "";

      return `<span data-label class="tag-size-custom inline-flex items-center justify-center font-body font-bold text-[0.625rem] leading-[0px] uppercase whitespace-nowrap rounded-full px-2 py-1 h-[1.125rem] ${this._labelVariantClass(
        variant
      )}">${this._escapeHtml(trimmed)}</span>`;
    }

    _labelVariantClass(variant) {
      switch (variant) {
        case "dark":
          return "bg-secondary text-white";
        case "danger":
          return "bg-white text-primary border border-primary";
        case "disabled":
          return "bg-gray-100 text-gray-900";
        case "primary":
        default:
          return "bg-white text-black border border-gray-100";
      }
    }
    _updateContent(html) {
      if (!html) {
        if (this._lastHTML !== null) {
          this.innerHTML = "";
          this._lastHTML = null;
        }
        return;
      }

      if (html !== this._lastHTML) {
        this.innerHTML = html;
        this._lastHTML = html;
      }
    }

    _stripVariantId(id) {
      if (!id) return "";
      const str = String(id);
      return str.includes("/") ? str.split("/").pop() : str;
    }

    _toVariantGid(id) {
      const str = String(id);
      return str.startsWith("gid://")
        ? str
        : `gid://shopify/ProductVariant/${str}`;
    }

    _normalizeLabelKey(value) {
      return String(value || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    }

    _escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    }

    _isNewProduct(openToShipDate) {
      if (!openToShipDate) return false;
      
      const shipDate = new Date(openToShipDate);
      if (isNaN(shipDate.getTime())) return false;
      
      const now = new Date();
      const sixMonthsInSeconds = 15768000;
      const differenceInSeconds = Math.floor((now.getTime() - shipDate.getTime()) / 1000);

      return differenceInSeconds >= 0 && differenceInSeconds <= sixMonthsInSeconds;
    }

    _getStorefrontToken() {
      if (this._storefrontToken !== undefined) {
        return this._storefrontToken;
      }
      const tokenElement = document.querySelector(".rcc-search");
      const token = tokenElement?.dataset?.token
        ? String(tokenElement.dataset.token).trim()
        : "";
      this._storefrontToken = token || null;
      return this._storefrontToken;
    }
  }

  customElements.define("rcc-product-card-labels", ProductCardLabelsComponent);
})();
